import React, { useEffect, useRef, useState } from 'react';
import { Alert, Box, Paper, Toolbar } from '@mui/material';
import Config from '../../config';
import AISparklesIcon from '../../components/AIStarsIcon';
import { resetThoughtSpotAuth } from './thoughtspot_auth_reset';

type ThoughtSpotConfig = {
    thoughtSpot?: {
        host?: string;
        worksheetId?: string;
    };
};

type AskDataProps = {
    setTitle?: (title: string) => void;
    // LB-480: embed token, kept fresh by the App.js refresh timer; re-embeds on change.
    thoughtSpotEmbed?: {
        EmbedToken?: string | null;
    } | null;
};

type SpotterInstance = {
    render: () => Promise<unknown>;
    destroy?: () => void;
};

// --- LB-405: real values come from Config.thoughtSpot (set per environment) ---
// host:        ThoughtSpot instance URL, e.g. https://myorg.thoughtspot.cloud
// worksheetId: the Spotter data source -- a Model/View/Table GUID, or 'auto_mode'
//              (LB-505) to let Spotter pick across all models. A GUID here is NOT a
//              Liveboard GUID -- confirm it with the ThoughtSpot/DWH team before ship.
// Read at call time so the page fails closed when either is missing.
function getThoughtSpotConfig() {
    const ts = (Config as ThoughtSpotConfig).thoughtSpot || {};
    return {
        host: ts.host || '',
        worksheetId: ts.worksheetId || '',
    };
}

let thoughtSpotSdkPromise: Promise<typeof import('./thoughtspot_sdk')> | null = null;
let resetThoughtSpotSdkForTests: (() => void) | null = null;

const loadThoughtSpotSdk = async (): Promise<typeof import('./thoughtspot_sdk')> => {
    if (!thoughtSpotSdkPromise) {
        thoughtSpotSdkPromise = import('./thoughtspot_sdk');
    }
    const sdk = await thoughtSpotSdkPromise;
    resetThoughtSpotSdkForTests = sdk.__resetThoughtSpotSdkForTests;
    return sdk;
};

const AUTH_ERROR_MESSAGE =
    'Unable to load Reports Agent right now. The Reporting Agent ' +
    'could not be reached. Please try again later.';
const LOAD_ERROR_MESSAGE =
    'Reports Agent failed to load. The Reporting Agent could not be ' +
    'reached. Please try again later.';
const INIT_ERROR_MESSAGE =
    'Unable to initialize Reports Agent. The Reporting Agent is not ' +
    'available right now. Please try again later.';

const logThoughtSpotAskDataError = (
    phase: string,
    host: string,
    worksheetId: string,
    error: unknown
): void => {
    // LB-480 review: `error` may be an AxiosError whose `config` carries the portal
    // session bearer token and whose `response.data` carries GetCompanyInfo PII.
    // Log a sanitized summary only (message + HTTP status) -- never the raw error.
    const message = error instanceof Error ? error.message : String(error);
    const status = (error as { response?: { status?: number } } | undefined)?.response?.status;
    console.error('ThoughtSpot Ask Data embed error', {
        phase,
        host,
        worksheetId,
        message,
        status,
    });
};

// Test-only hook: reset the module-level once-guard so each test starts from a
// clean "SDK not yet initialized" baseline. Not used by production code.
export const __resetSdkInitForTests = (): void => {
    if (resetThoughtSpotSdkForTests) {
        resetThoughtSpotSdkForTests();
    }
    thoughtSpotSdkPromise = null;
    resetThoughtSpotSdkForTests = null;
};

const AskData = ({ setTitle, thoughtSpotEmbed }: AskDataProps) => {
    const [error, setError] = useState<string | null>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const spotterRef = useRef<SpotterInstance | null>(null);

    useEffect(() => {
        if (setTitle) {
            setTitle('Reports Agent');
        }
    }, [setTitle]);

    // LB-481: hide Frigade's fixed "Get help now!" launcher on this page so it
    // does not overlap the Spotter "Enter your question" input. Scoped via a body
    // class that App.css targets; removed on unmount so every other page keeps it.
    useEffect(() => {
        document.body.classList.add('hide-frigade-launcher');
        return () => {
            document.body.classList.remove('hide-frigade-launcher');
        };
    }, []);

    const { host, worksheetId } = getThoughtSpotConfig();
    const isConfigured = !!(host && worksheetId);
    // Re-embedding is keyed on this value, so a new prop object with the same token is a no-op.
    const authToken = (thoughtSpotEmbed || {}).EmbedToken || '';

    useEffect(() => {
        let isMounted = true;
        let unregisterAuthError: (() => void) | null = null;
        const setAskDataError = (message: string): void => {
            if (isMounted) {
                setError(message);
            }
        };

        const { host, worksheetId } = getThoughtSpotConfig();
        // Fail closed: without a host + datasource the Spotter embed has nothing
        // to query, so don't initialise it -- render() shows a not-configured
        // notice.
        if (!host || !worksheetId) {
            return () => {
                isMounted = false;
            };
        }

        const initializeSpotter = async (): Promise<void> => {
            try {
                const [{ SpotterEmbed }, thoughtSpotSdk] = await Promise.all([
                    import('@thoughtspot/visual-embed-sdk'),
                    loadThoughtSpotSdk(),
                ]);
                if (!isMounted) {
                    return;
                }
                unregisterAuthError = thoughtSpotSdk.registerThoughtSpotAuthErrorHandler(
                    (error) => {
                        logThoughtSpotAskDataError('auth-token', host, worksheetId, error);
                        setAskDataError(AUTH_ERROR_MESSAGE);
                    }
                );
                // LB-480: drop the SDK's cached token so the refreshed one is used, not the stale one.
                resetThoughtSpotAuth();
                thoughtSpotSdk.initThoughtSpot(host, authToken);
                const container = containerRef.current;
                if (container) {
                    // worksheetId is the SpotterEmbedViewConfig data-source key, verified
                    // against @thoughtspot/visual-embed-sdk 1.49.1
                    // (lib/src/embed/conversation.d.ts: `worksheetId: string`).
                    // LB-497: spotterChatConfig hides/relabels the ThoughtSpot branding on
                    // the tool-response cards shown while a query runs (SpotterChatViewConfig,
                    // read off the SpotterEmbed viewConfig -- not an init() customization).
                    // LB-503: spotterSidebarConfig enables the native Chat History Sidebar so
                    // results persist (server-side, per user) across navigate-away.
                    spotterRef.current = new SpotterEmbed(container, {
                        worksheetId,
                        spotterChatConfig: thoughtSpotSdk.SPOTTER_CHAT_CONFIG,
                        spotterSidebarConfig: thoughtSpotSdk.SPOTTER_SIDEBAR_CONFIG,
                    });
                    // render() returns a Promise; a rejection (bad token, network, SDK
                    // error) must surface a user-visible failure notice rather than an
                    // unhandled rejection.
                    spotterRef.current.render().catch((e: unknown) => {
                        logThoughtSpotAskDataError('spotter-render', host, worksheetId, e);
                        setAskDataError(LOAD_ERROR_MESSAGE);
                    });
                }
            } catch (e) {
                logThoughtSpotAskDataError('sdk-init', host, worksheetId, e);
                setAskDataError(INIT_ERROR_MESSAGE);
            }
        };

        initializeSpotter();

        return () => {
            isMounted = false;
            if (unregisterAuthError) {
                unregisterAuthError();
            }
            if (spotterRef.current && typeof spotterRef.current.destroy === 'function') {
                spotterRef.current.destroy();
                spotterRef.current = null;
            }
        };
    }, [host, worksheetId, authToken]);

    return (
        <Paper sx={{ overflow: 'hidden' }}>
            <Toolbar className='lbtoolbar'>
                <span style={{ flex: 1, textAlign: 'left', display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                    <span>Reports Agent</span>
                    <span
                        role="img"
                        aria-label="AI-powered"
                        style={{ display: 'inline-flex', alignItems: 'center' }}
                    >
                        <AISparklesIcon size={22} bgColor="transparent" />
                    </span>
                </span>
            </Toolbar>
            {!isConfigured
                ? <div data-testid="ask-data-unconfigured" style={{ padding: 24 }}>
                    Reports Agent is not yet available in this environment. The Agent
                    connection has not been configured.
                </div>
                : error
                    ? <Box data-testid="ask-data-error" sx={{ p: 2 }}>
                        <Alert severity="info">{error}</Alert>
                    </Box>
                    // LB-481: 138px = 60 (top menu) + 14 (.app-content top pad) + 50 (toolbar)
                    // + 14 (.app-content bottom pad), so the embed fills the viewport while keeping
                    // the standard 14px page margin below the Paper (was 112px, which overflowed).
                    : <div ref={containerRef} id="tsSpotterEmbed" data-testid="tsSpotterEmbed" style={{ height: 'calc(100vh - 138px)' }} />}
        </Paper>
    );
};

export default AskData;
